<template>
  <div class="exec">
    <div class="exec-header">
      <div>
        <h5 class="exec-title">Execuções recentes</h5>
        <p class="exec-hint">Cada disparo deste gatilho é registrado aqui — sucesso, falha ou skip — com o payload do evento.</p>
      </div>
      <div class="exec-header__actions">
        <select class="form-control exec-filter" v-model="filtroResult" @change="load(1)">
          <option value="">Todos</option>
          <option value="success">Sucesso</option>
          <option value="failure">Falha</option>
          <option value="skipped">Skip</option>
        </select>
        <button type="button" class="exec-btn exec-btn--ghost" @click="load(meta.current_page)" title="Recarregar">
          <fa-icon icon="sync-alt" :spin="loading" />
        </button>
      </div>
    </div>

    <div v-if="loading && !items.length" class="exec-loading">
      <fa-icon icon="spinner" spin /> Carregando execuções...
    </div>

    <div v-else-if="!filtered.length" class="exec-empty">
      <fa-icon icon="play-circle" class="exec-empty__icon" />
      <p class="exec-empty__title">Nenhuma execução registrada</p>
      <p class="exec-empty__hint">Quando o gatilho disparar, os logs aparecerão aqui.</p>
    </div>

    <div v-else class="exec-list">
      <div v-for="log in filtered" :key="log.id" class="exec-item" :class="`exec-item--${log.result}`">
        <div class="exec-item__rail">
          <fa-icon :icon="iconFor(log.result)" />
        </div>
        <div class="exec-item__body">
          <div class="exec-item__meta">
            <span class="exec-item__badge" :class="`exec-item__badge--${log.result}`">{{ labelFor(log.result) }}</span>
            <span class="exec-item__date"><fa-icon icon="clock" /> {{ formatarData(log.executed_at) }}</span>
            <span v-if="log.duration_ms != null" class="exec-item__duration"><fa-icon icon="stopwatch" /> {{ log.duration_ms }} ms</span>
          </div>

          <div v-if="log.error_message" class="exec-item__error">
            <strong>Erro:</strong> {{ log.error_message }}
          </div>

          <details v-if="log.event_data" class="exec-item__details">
            <summary>
              <fa-icon icon="code" /> Payload do evento
            </summary>
            <pre class="exec-item__payload">{{ formatPayload(log.event_data) }}</pre>
          </details>

          <div class="exec-item__actions">
            <button type="button" class="exec-btn exec-btn--secondary" @click="$emit('replay', log)" :disabled="!log.event_data">
              <fa-icon icon="redo" /> Reexecutar com este payload
            </button>
            <button type="button" class="exec-btn exec-btn--ghost" @click="$emit('use-payload', log)" :disabled="!log.event_data">
              <fa-icon icon="vial" /> Usar no painel de teste
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="meta.last_page > 1" class="exec-pagination">
      <button class="exec-page-btn" :disabled="meta.current_page <= 1" @click="load(meta.current_page - 1)">
        <fa-icon icon="chevron-left" />
      </button>
      <span class="exec-pagination__info">Página <strong>{{ meta.current_page }}</strong> de <strong>{{ meta.last_page }}</strong></span>
      <button class="exec-page-btn" :disabled="meta.current_page >= meta.last_page" @click="load(meta.current_page + 1)">
        <fa-icon icon="chevron-right" />
      </button>
    </div>
  </div>
</template>

<script>
import { get } from '@/helpers/apiRequest';

const RESULT_LABELS = { success: 'Sucesso', failure: 'Falha', skipped: 'Skip' };
const RESULT_ICONS  = { success: 'check-circle', failure: 'exclamation-triangle', skipped: 'minus-circle' };

export default {
  name: 'AcaoObjetoExecucoesTab',
  props: {
    endpointBase: { type: String, required: true },
    acaoId: { type: [Number, String], default: null },
  },
  data() {
    return {
      items: [],
      meta: { current_page: 1, last_page: 1 },
      loading: false,
      filtroResult: '',
    };
  },
  computed: {
    filtered() {
      if (!this.filtroResult) return this.items;
      return this.items.filter(i => i.result === this.filtroResult);
    },
  },
  watch: {
    acaoId: { immediate: true, handler() { this.load(1); } },
  },
  methods: {
    load(page = 1) {
      if (!this.acaoId) { this.items = []; return; }
      this.loading = true;
      get(`${this.endpointBase}/${this.acaoId}/execucoes?page=${page}`)
        .then(json => {
          this.items = json.data || json.itens || (Array.isArray(json) ? json : []);
          const p = json.meta || json.paginator || {};
          this.meta = {
            current_page: p.current_page || p.currentPage || 1,
            last_page:    p.last_page    || p.lastPage    || 1,
          };
          this.loading = false;
        })
        .catch(() => { this.loading = false; });
    },
    labelFor(r) { return RESULT_LABELS[r] || r; },
    iconFor(r)  { return RESULT_ICONS[r]  || 'circle'; },
    formatarData(s) {
      if (!s) return '—';
      try { return new Date(s).toLocaleString('pt-BR'); } catch (_) { return s; }
    },
    formatPayload(p) {
      try { return JSON.stringify(p, null, 2); } catch (_) { return String(p); }
    },
  },
};
</script>

<style scoped>
.exec-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
.exec-title { margin: 0; font-size: 13px; font-weight: 700; color: #1d2939; text-transform: uppercase; letter-spacing: 0.4px; }
.exec-hint { margin: 2px 0 0; font-size: 12px; color: #667085; }
.exec-header__actions { display: flex; gap: 8px; }
.exec-filter { width: 140px; height: 34px; font-size: 12px; border-radius: 6px; border: 1px solid #dddce9; }

.exec-loading, .exec-empty { text-align: center; padding: 32px; color: #98a2b3; }
.exec-empty__icon { font-size: 30px; color: #d0d5dd; margin-bottom: 10px; opacity: 0.6; }
.exec-empty__title { font-size: 13px; font-weight: 600; color: #475467; margin: 6px 0 2px; }
.exec-empty__hint { font-size: 12px; color: #98a2b3; margin: 0; }

.exec-list { display: flex; flex-direction: column; gap: 10px; }
.exec-item {
  display: flex;
  gap: 12px;
  background: #fff;
  border: 1px solid #e4e7eb;
  border-left-width: 4px;
  border-radius: 8px;
  padding: 12px 14px;
}
.exec-item--success { border-left-color: #5eb95e; }
.exec-item--failure { border-left-color: #d9534f; }
.exec-item--skipped { border-left-color: #98a2b3; }

.exec-item__rail {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}
.exec-item--success .exec-item__rail { background: #ecfaee; color: #2e7d32; }
.exec-item--failure .exec-item__rail { background: #fdecea; color: #b3261e; }
.exec-item--skipped .exec-item__rail { background: #f2f4f7; color: #667085; }

.exec-item__body { flex: 1; min-width: 0; }
.exec-item__meta { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; font-size: 11px; color: #667085; margin-bottom: 8px; }
.exec-item__badge { padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }
.exec-item__badge--success { background: #ecfaee; color: #2e7d32; }
.exec-item__badge--failure { background: #fdecea; color: #b3261e; }
.exec-item__badge--skipped { background: #f2f4f7; color: #475467; }
.exec-item__date, .exec-item__duration { display: inline-flex; align-items: center; gap: 4px; }

.exec-item__error {
  background: #fdecea;
  color: #8a1f18;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  margin-bottom: 8px;
  word-break: break-word;
}
.exec-item__details summary {
  cursor: pointer;
  font-size: 12px;
  color: #3a3e81;
  font-weight: 600;
  padding: 4px 0;
}
.exec-item__payload {
  background: #fafbfc;
  border: 1px solid #f2f4f7;
  border-radius: 6px;
  padding: 8px 10px;
  margin: 6px 0 0;
  max-height: 220px;
  overflow: auto;
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 11px;
  color: #1d2939;
  white-space: pre-wrap;
}

.exec-item__actions { margin-top: 10px; display: flex; gap: 8px; flex-wrap: wrap; }
.exec-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  font-family: inherit;
  transition: all 0.15s ease;
}
.exec-btn--secondary { background: #edf8f9; color: #2298a3; border-color: #c5e6e9; }
.exec-btn--secondary:hover:not(:disabled) { background: #2298a3; color: #fff; }
.exec-btn--ghost { background: #fff; border-color: #dddce9; color: #475467; }
.exec-btn--ghost:hover:not(:disabled) { border-color: #3a3e81; color: #3a3e81; }
.exec-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.exec-pagination { display: flex; justify-content: center; align-items: center; gap: 12px; margin-top: 14px; }
.exec-page-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  background: #fff;
  color: #475467;
  cursor: pointer;
}
.exec-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.exec-pagination__info { font-size: 12px; color: #667085; }
</style>
